import React from 'react';
import { 
  Grid, 
  Box, 
  Button, 
  Tooltip,
  CircularProgress,
  Typography,
  Alert
} from '@mui/material';
import {
  FormatListNumbered as FormatListNumberedIcon,
  AccessTime as AccessTimeIcon,
  EventBusy as EventBusyIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import StatsCard from './StatsCard';

const DashboardStats = ({ 
  estatisticas, 
  carregandoEstatisticas, 
  customTheme, 
  atualizarDadosUsuario, 
  atualizandoUsuario,
  senhasNaFilaAtendente = 0,
  sidebarOpen = false,
  onEmitirSenha,
  senhaChamada = null,
  atendimentoIniciado = false,
  atendimentoFinalizado = false
}) => {
  // Determinar se o botão deve estar desabilitado
  // Só desabilitar se há senha ativa (não finalizada) ou atendimento em andamento
  const temSenhaAtiva = senhaChamada && !atendimentoFinalizado;
  const botaoDesabilitado = temSenhaAtiva || atendimentoIniciado;

  return (
    <Box sx={{ mb: { xs: 3, sm: 4, md: 4 } }}>
      {/* Header com botão de atualizar */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', md: 'flex-start' }, 
        mb: { xs: 2.5, sm: 3, md: 3 },
        gap: { xs: 1.5, sm: 2, md: 2 }
      }}>
        {/* Informação sobre o botão atualizar */}
        <Alert 
          icon={<InfoIcon fontSize="inherit" />} 
          severity="info" 
          sx={{ 
            flex: 1,
            maxWidth: { xs: '100%', md: sidebarOpen ? '600px' : '800px' },
            '& .MuiAlert-message': {
              fontSize: { xs: '0.8125rem', sm: '0.875rem' }
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Serviços atualizados recentemente?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use o botão "Atualizar Dados" para recarregar seus serviços atendidos sem precisar sair do sistema.
          </Typography>
        </Alert>
        
        {/* Botões de ação */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'center', md: 'flex-end' },
          gap: { xs: 1, sm: 1.5 }
        }}>
          {/* Botão Emitir Senha */}
          <Tooltip 
            title={
              botaoDesabilitado
                ? "Você já possui uma senha ativa. Finalize o atendimento atual antes de emitir uma nova senha."
                : atendimentoFinalizado
                  ? "Emitir uma nova senha para atendimento (a senha anterior foi finalizada)"
                  : "Emitir uma nova senha para atendimento"
            }
            placement="left"
          >
            <span> {/* Wrapper span necessário para Tooltip funcionar com botão desabilitado */}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onEmitirSenha}
                disabled={botaoDesabilitado}
                sx={{
                  borderColor: customTheme.palette.secondary.main,
                  color: customTheme.palette.secondary.main,
                  fontWeight: 500,
                  textTransform: 'none',
                  px: { xs: 2.5, sm: 3 },
                  py: { xs: 1, sm: 1.2 },
                  minWidth: { xs: '180px', sm: '200px', md: 'auto' },
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  '&:hover': {
                    borderColor: customTheme.palette.secondary.dark,
                    backgroundColor: customTheme.palette.secondary.main,
                    color: 'white',
                  },
                  '&:disabled': {
                    borderColor: customTheme.palette.action.disabled,
                    color: customTheme.palette.action.disabled,
                    opacity: 0.6,
                  },
                }}
              >
                Emitir uma senha
              </Button>
            </span>
          </Tooltip>
        
        {/* Botão de atualizar */}
          <Tooltip 
            title="Recarrega seus serviços atendidos e a fila de senhas com base nas últimas configurações"
            placement="left"
          >
            <Button
              variant="contained"
              startIcon={atualizandoUsuario ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RefreshIcon />
              )}
              onClick={atualizarDadosUsuario}
              disabled={atualizandoUsuario}
              sx={{
                backgroundColor: customTheme.palette.primary.main,
                color: 'white',
                fontWeight: 500,
                textTransform: 'none',
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1, sm: 1.2 },
                boxShadow: 2,
                minWidth: { xs: '180px', sm: '200px', md: 'auto' },
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                '&:hover': {
                  backgroundColor: customTheme.palette.primary.dark,
                  boxShadow: 3,
                },
                '&:disabled': {
                  backgroundColor: customTheme.palette.action.disabled,
                  color: customTheme.palette.action.disabled,
                }
              }}
            >
              {atualizandoUsuario ? 'Atualizando...' : 'Atualizar Dados'}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Grid das estatísticas */}
      <Grid container spacing={{ xs: 2, sm: sidebarOpen ? 2 : 3, md: 3 }}>
        <Grid item 
          xs={12} 
          sm={sidebarOpen ? 6 : 6} 
          md={sidebarOpen ? 6 : 3}
          lg={sidebarOpen ? 3 : 3}
        >
          <StatsCard
            title="Senhas na Fila"
            value={senhasNaFilaAtendente}
            icon={<FormatListNumberedIcon />}
            color={customTheme.palette.primary.main}
            customTheme={customTheme}
            loading={carregandoEstatisticas}
          />
        </Grid>
        <Grid item 
          xs={12} 
          sm={sidebarOpen ? 6 : 6} 
          md={sidebarOpen ? 6 : 3}
          lg={sidebarOpen ? 3 : 3}
        >
          <StatsCard
            title="Tempo Médio"
            value={estatisticas.tempoMedioAtendimento}
            icon={<AccessTimeIcon />}
            color={customTheme.palette.info.main}
            customTheme={customTheme}
            subtitle="por atendimento"
            loading={carregandoEstatisticas}
          />
        </Grid>
        <Grid item 
          xs={12} 
          sm={sidebarOpen ? 6 : 6} 
          md={sidebarOpen ? 6 : 3}
          lg={sidebarOpen ? 3 : 3}
        >
          <StatsCard
            title="Não Comparecimentos"
            value={estatisticas.naoComparecimentos}
            icon={<EventBusyIcon />}
            color={customTheme.palette.warning.main}
            customTheme={customTheme}
            loading={carregandoEstatisticas}
          />
        </Grid>
        <Grid item 
          xs={12} 
          sm={sidebarOpen ? 6 : 6} 
          md={sidebarOpen ? 6 : 3}
          lg={sidebarOpen ? 3 : 3}
        >
          <StatsCard
            title="Atendimentos Hoje"
            value={estatisticas.atendimentosHoje}
            icon={<AssignmentTurnedInIcon />}
            color={customTheme.palette.success.main}
            customTheme={customTheme}
            loading={carregandoEstatisticas}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardStats; 